import React from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import isValid from 'date-fns/isValid';
import camelCase from 'lodash/camelCase';
import CustomFieldTypes from '@/constants/customFields/CustomFieldTypes';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import useConstituent from '@/lib/hooks/swr/useConstituent';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import useLabels from '@/lib/hooks/swr/useLabels';
import usePreselectedLabels from '@/lib/hooks/swr/usePreselectedLabels';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import { ConstituentType } from '@/lib/types/ConstituentPropType';
import { createConstituent } from '@/services/constituentServices';
import {
  dateStringToDate,
  formatDateToString,
  parseDateValue,
} from '@/utils/dates';
import ConstituentField from './constants/ConstituentField';
import OrgDefaults from './constants/OrgDefaults';
import PersonDefaults from './constants/PersonDefaults';
import TemporaryField from './constants/TemporaryField';
import mapDataForApi from './mapDataForApi';

const getDefaults = (constituentType) => ({
  ...(constituentType === ConstituentType.ORG ? OrgDefaults : PersonDefaults),
  constituentType,
});

const useConstituentEditForm = ({ constituentId, constituentType }) => {
  const ownerOrgId = useOwnerOrgId();
  const {
    data,
    lockedAttributes: lockedAttributesApi,
    updateConstituent,
  } = useConstituent(constituentId);
  const { data: customFieldData } = useCustomFields();
  const history = useHistory();
  const [lockedAttributes, setLockedAttributes] = React.useState(
    new Set(lockedAttributesApi?.map(camelCase) ?? []),
  );

  React.useEffect(() => {
    setLockedAttributes(new Set(lockedAttributesApi?.map(camelCase) ?? []));
  }, [lockedAttributesApi]);

  const defaultValues = React.useMemo(() => {
    let newDefaults = getDefaults(constituentType);
    if (data) {
      newDefaults = Object.entries(newDefaults).reduce(
        (acc, [key, defaultValue]) => {
          const newValue = data[key] || defaultValue;

          if (key === TemporaryField.BIRTHDAY && data[ConstituentField.DOB]) {
            return {
              ...acc,
              [key]: parseDateValue(data[ConstituentField.DOB]),
            };
          }

          if (key === TemporaryField.UPLOAD_IDS) {
            return {
              ...acc,
              [key]: (data[ConstituentField.ATTACHMENTS] || []).map(
                ({ uploadId }) => uploadId,
              ),
            };
          }

          if (key === ConstituentField.CUSTOM_FIELDS) {
            return {
              ...acc,
              [key]: (data.customFields || []).reduce(
                (obj, { customFieldId, value }) => {
                  const isDateField =
                    (customFieldData || []).find(
                      (field) => field.customFieldId === customFieldId,
                    )?.fieldType === CustomFieldTypes.DATE_PICKER;

                  return {
                    ...obj,
                    [customFieldId]: isDateField
                      ? dateStringToDate(value)
                      : value,
                  };
                },
                {},
              ),
            };
          }

          return { ...acc, [key]: newValue };
        },
        {},
      );
    }
    return newDefaults;
  }, [data, constituentType, customFieldData]);

  const formMethods = useForm({ defaultValues, mode: 'onBlur' });
  const { getValues, handleSubmit, setError, setFocus, setValue, watch } =
    formMethods;

  // Focus on a field if it's named in the location hash. The address is a
  // special case since it uses the Google autocomplete widget. We'll deal with
  // that in its individual component.
  const { hash } = useLocation();
  const [hasInitiallyRendered, setHasInitiallyRendered] = React.useReducer(
    () => true,
    false,
  );
  React.useEffect(() => {
    if (!hash) return;
    const fieldName = hash.slice(1);
    if (getValues(fieldName) === undefined) return;

    // Let the form fully render so all necessary fields are present.
    if (hasInitiallyRendered) {
      setFocus(fieldName);
    } else {
      setHasInitiallyRendered();
    }
  }, [hash, hasInitiallyRendered, setFocus]);

  // Set up automatically updating the display name if it hasn't been manually
  // edited as the user updates the first/last or org name.
  const firstName = watch(ConstituentField.FIRST_NAME);
  const lastName = watch(ConstituentField.LAST_NAME);
  const orgName = watch(ConstituentField.ORG_NAME);
  const fullName =
    constituentType === ConstituentType.ORG
      ? orgName
      : [firstName, lastName]
          .filter(Boolean)
          .map((str) => str.trim())
          .join(' ');

  React.useEffect(() => {
    setValue(ConstituentField.DISPLAY_NAME, fullName);
  }, [fullName]);

  // Birthday in this form is tracked as a Date object in a temporary field.
  // As that field is updated, we want to correctly format it for the API as a
  // date string.
  const birthday = watch(TemporaryField.BIRTHDAY);
  React.useEffect(() => {
    if (!(ConstituentField.DOB in defaultValues) || !isValid(birthday)) return;
    setValue(
      ConstituentField.DOB,
      birthday ? formatDateToString(birthday) : '',
    );
  }, [birthday]);

  // Labels from the API are represented as an array of IDs. We need to fetch
  // data for any of those labels and populate it to our temporary labels field.
  const labelIds = watch(`${ConstituentField.LABEL_IDS}.tags`);
  const { data: preSelectedLabels } = usePreselectedLabels(labelIds);
  React.useEffect(() => {
    if (preSelectedLabels) {
      setValue(TemporaryField.LABELS, preSelectedLabels, {
        shouldTouch: false,
      });
    }
  }, [preSelectedLabels]);

  // Submit handler.
  const { create: createLabel } = useLabels(null);
  const getLabelIds = async (labels) => {
    const newLabels = await Promise.all(
      labels.map((item) =>
        item.labelId
          ? item
          : createLabel({ labelName: item.labelName, labelType: 'tag' }),
      ),
    );
    return newLabels.map(({ labelId }) => labelId);
  };

  const onSubmit = React.useCallback(
    (e) =>
      handleSubmit(async (formData) => {
        const formattedData = mapDataForApi(formData);
        // For custom fields, single select values should be an array of one.
        // Existing values will reflect this, but if they have been edited in
        // the form, they will be represented at this point as a string and
        // need to be converted.
        // TODO(jon): Custom fields should probably be treated similar to other
        // temporary fields in constituent edit form and namespaced in a way
        // that doesn't conflict with incoming API data. That or we need to
        // update the CustomFieldSelect to properly set single array values.
        if (formattedData[ConstituentField.CUSTOM_FIELDS]) {
          formattedData[ConstituentField.CUSTOM_FIELDS] = formattedData[
            ConstituentField.CUSTOM_FIELDS
          ].map((field) =>
            customFieldData.find(
              (item) => item.customFieldId === field.custom_field_id,
            )?.fieldType === CustomFieldTypes.LIST_SINGLE &&
            !Array.isArray(field.value)
              ? {
                  ...field,
                  value: [field.value],
                }
              : field,
          );
        }

        // Map label objects to label IDs, creating new labels as necessary.
        const newLabelIds = await getLabelIds(formData[TemporaryField.LABELS]);
        formattedData[ConstituentField.LABEL_IDS].tags = newLabelIds;

        try {
          let updatedConstituentId = constituentId;
          if (constituentId) {
            await updateConstituent(formattedData);
          } else {
            const updatedConstituent = await createConstituent({
              data: formattedData,
              ownerOrgId,
            });
            updatedConstituentId =
              updatedConstituent?.data?.constituent?.constituentId;
          }
          history.push(
            generatePath(AuthRoutes.CONSTITUENT_INFO, {
              constituentId: updatedConstituentId,
            }),
          );
        } catch (error) {
          setError('root.serverError', {
            message: error?.message || translate('API_ERROR_UNKNOWN'),
          });
        }
      })(e),
    [customFieldData, handleSubmit, updateConstituent],
  );

  return {
    extSource: data?.extSource,
    formMethods,
    lockedAttributes,
    onSubmit,
  };
};

export default useConstituentEditForm;
